import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import ListPager, { LIST_PAGE_SIZE } from '@client/components/ListPager'
import { useTranslation } from 'react-i18next'
import useQuery from '@/client/utils/useQuery'
import { useLayout, TContextMenuItem } from '@/client/components/Layout/LayoutManager'
import { Table } from '../../components/Controls'
import TenantModel, { ETenantStatus, getTenantLabel, TTenant } from '../../models/tenant'
import { createDefaultListContextMenuItems } from '@/client/utils/listContextMenuDefaults'
import { TenantIcon } from '@/client/config'
import EditTenant from './EditTenant'

//console.log('Load TenantList ...')

const tenantModel = new TenantModel()

export default function SidebarTenants() {
    const { t } = useTranslation()
    const { addTab, onContextMenu, openTabToRight, removeTabCopies, removeOtherTabs } = useLayout()
    const [page, setPage] = useState<number>(1)
    // useQuery loads again when this counter changes, not when its args do
    const [reload, setReload] = useState<number>(0)
    const { loading, error, data } = useQuery(tenantModel.list, { args: { page, count: LIST_PAGE_SIZE }, refreshCounter: reload })
    const canSort = data?.rows && data.rows.length > 0
    const [rows, setRows] = useState<TTenant[]>([])

    useEffect(() => {
        if (data?.rows) {
            setRows(data.rows)
        }
    }, [data])

    const handleTenantCreated = (newTenant: any) => {
        // existing logic (still marked as TODO in your comment)
        setTimeout(() => {
            let replaced = false
            const newRows = rows.map((row) => {
                if (row._id === newTenant._id) {
                    replaced = true
                    return newTenant
                }
                return row
            })
            if (!replaced) newRows.push(newTenant)
            setRows(newRows)
        }, 100)
    }

    const controller = {
        defaultRow(params?: any) {
            return {
                _id: '',
                name: '',
                slug: '',
                ownerId: '',
                owner: {
                    id: '',
                    userName: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                },
                subscriptionId: '',
                subscription: {
                    _id: '',
                    status: '',
                    maxActiveUsers: 1,
                    maxActiveProjects: 1,
                    startedAt: '',
                    expiredAt: '#',
                    externalId: '',
                    createdAt: '',
                    updatedAt: '',
                    blockedReason: '',
                    terminatedAt: '',
                },
                status: ETenantStatus.Active,
            } as any
        },

        getTabContent(params: any) {
            const { tab } = params
            return <EditTenant tabId={tab.id} onCreated={handleTenantCreated} />
        },

        useProjectInBreadCrumb(params: any) {
            return false
        },

        getBreadCrumb(params: any) {
            const { tab } = params
            const breadCrumb: string[] = [t('Tenant'), tab.name]
            return breadCrumb
        },

        ignoreProjectExists(params: any) {
            return true
        },
    }

    function newTab(row: TTenant) {
        let name
        if (!row._id) {
            name = t('New tenant')
        } else {
            // name = row.user.firstName ? row.user.firstName[0] + '.' : ''
            // if (name && row.user.lastName) name += ' '
            // if (row.user.lastName) name += row.user.lastName
            name = row.name
        }

        return {
            dataId: `tenant-${row._id}`,
            name,
            component: 'tenant',
            controller,
            icon: <TenantIcon />,
            store: {
                id: row._id,
                initialData: row,
                formData: row,
            },
        }
    }

    function onOpenTab(row: TTenant) {
        addTab(newTab(row))
    }

    function onRenderLoading(loading: ReactNode): ReactNode {
        return null
    }

    function onRenderEmpty() {
        return <div className="rs-table-body-info">{t('No tenant found.')}</div>
    }

    const getTenantById = useCallback(
        (id: string): TTenant | null => {
            return rows.find((s) => s._id === id) || null
        },
        [rows]
    )

    const baseMenuItems = useMemo<TContextMenuItem[]>(() => {
        return createDefaultListContextMenuItems<TTenant>({
            t,
            getItemById: getTenantById,
            addTab,
            openTabToRight,
            createTab: newTab,
            removeTabCopies,
            removeOtherTabs,
        })
    }, [t, getTenantById, addTab, openTabToRight, removeTabCopies, removeOtherTabs])

    const contextMenuItems = baseMenuItems

    return (
        <div className="sidebar-content sidebar-content-tenants">
            {/* No "Add tenant": tenants come from the shop or with a new owner;
                the button was only there for testing (operator, 13.10.2026). */}
            {<Table
                className={'tablist-table' + (rows.length == 0 ? ' empty-list' : '')}
                loading={loading}
                wordWrap="break-word"
                fillHeight
                data={rows}
                renderEmpty={onRenderEmpty}
                renderLoading={onRenderLoading}
                onRowClick={onOpenTab}
            >
                <Table.Column align="left" sortable={canSort} flexGrow={1}>
                    <Table.HeaderCell className="name">{t('User')}</Table.HeaderCell>
                    <Table.Cell className="name" dataKey="user.lastName">
                        {(row: TTenant) => {
                            return row.name
                        }}
                    </Table.Cell>
                </Table.Column>
                <Table.Column className="status-color-wrapper" align="flex-end" width={50}>
                    <Table.HeaderCell className="status"> </Table.HeaderCell>
                    <Table.Cell className="status" dataKey="user.status">
                        {(row: TTenant) => {
                            return (
                                <span
                                    className={'status-color status-color-' + row.status}
                                    title={getTenantLabel(row.status)}
                                ></span>
                            )
                        }}
                    </Table.Cell>
                </Table.Column>
            </Table>}
            <ListPager total={Number(data?.total ?? 0)} page={page} onChangePage={(p) => { setPage(p); setReload((n) => n + 1) }} disabled={loading} />
        </div>
    )
}
